import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import DarkTemplate from './templates/DarkTemplate';
import MinimalTemplate from './templates/MinimalTemplate';
import BoutiqueTemplate from './templates/BoutiqueTemplate';
import VibrantTemplate from './templates/VibrantTemplate';
import GridTemplate from './templates/GridTemplate';

interface StoreProfile {
  store_name: string;
  store_description: string;
  banner_image: string;
  logo_image: string;
  template: string;
  social_links?: Array<{ id: string; platform: string; label: string; url: string; is_active: boolean }>;
}

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  category: string;
  cover_image: string;
  media_files: Array<{ url: string; type: 'image' | 'video'; name: string }> | null;
  file_url: string;
  product_type: string;
  stock_unlimited: boolean;
  stock_quantity: number;
  shipping_info: string;
  weight: number;
}

interface Profile {
  full_name: string;
  avatar_url: string;
}

interface GatewayOption {
  id: 'paystack' | 'flutterwave';
  name: string;
  icon: string;
  description: string;
  accentColor: string;
  bgColor: string;
  borderColor: string;
}

const allGateways: GatewayOption[] = [
  {
    id: 'paystack',
    name: 'Paystack',
    icon: 'ri-secure-payment-line',
    description: 'Cards, bank transfer, mobile money',
    accentColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-500',
  },
  {
    id: 'flutterwave',
    name: 'Flutterwave',
    icon: 'ri-exchange-funds-line',
    description: 'Cards, bank, mobile money, USSD',
    accentColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-500',
  },
];

function isVideoUrl(url: string): boolean {
  if (!url) return false;
  const lower = url.toLowerCase().split('?')[0];
  return lower.endsWith('.mp4') || lower.endsWith('.mov') || lower.endsWith('.webm') || lower.endsWith('.ogg');
}

function CoverMedia({ product, className, natural }: { product: Product; className?: string; natural?: boolean }) {
  const firstMedia = product.media_files && product.media_files.length > 0 ? product.media_files[0] : null;
  const isVideo = firstMedia ? firstMedia.type === 'video' : isVideoUrl(product.cover_image);
  const src = firstMedia ? firstMedia.url : product.cover_image;
  if (!src) return null;
  if (isVideo) {
    return (
      <video
        src={src}
        className={className}
        muted
        autoPlay
        loop
        playsInline
        preload="metadata"
        style={natural ? { width: '100%', height: 'auto', display: 'block' } : undefined}
      />
    );
  }
  return (
    <img
      src={src}
      alt={product.title}
      className={className}
      style={natural ? { width: '100%', height: 'auto', display: 'block' } : undefined}
    />
  );
}

const categoryIcons: Record<string, string> = {
  'e-book': 'ri-book-line', 'template': 'ri-file-text-line', 'course': 'ri-graduation-cap-line',
  'audio': 'ri-music-line', 'video': 'ri-video-line', 'software': 'ri-code-box-line',
  'clothing': 'ri-t-shirt-line', 'accessories': 'ri-handbag-line', 'art': 'ri-paint-brush-line',
  'electronics': 'ri-cpu-line', 'books': 'ri-book-open-line', 'other': 'ri-folder-line',
};

const LINK_PLATFORMS: Record<string, { icon: string; color: string }> = {
  website:   { icon: 'ri-global-line',           color: 'text-gray-600' },
  instagram: { icon: 'ri-instagram-line',         color: 'text-pink-500' },
  twitter:   { icon: 'ri-twitter-x-line',         color: 'text-gray-800' },
  facebook:  { icon: 'ri-facebook-circle-line',   color: 'text-blue-600' },
  tiktok:    { icon: 'ri-tiktok-line',            color: 'text-gray-900' },
  youtube:   { icon: 'ri-youtube-line',           color: 'text-red-500' },
  linkedin:  { icon: 'ri-linkedin-box-line',      color: 'text-blue-700' },
  whatsapp:  { icon: 'ri-whatsapp-line',          color: 'text-green-500' },
  telegram:  { icon: 'ri-telegram-line',          color: 'text-sky-500' },
  custom:    { icon: 'ri-links-line',             color: 'text-teal-500' },
};

export default function StorePage({ overrideUserId }: { overrideUserId?: string } = {}) {
  const { userId: paramUserId } = useParams<{ userId?: string }>();
  const userId = overrideUserId || paramUserId;

  const [resolvedUserId, setResolvedUserId] = useState<string | null>(userId || null);
  const [storeProfile, setStoreProfile] = useState<StoreProfile | null>(null);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'digital' | 'physical'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [domainNotFound, setDomainNotFound] = useState(false);

  // Buyer details
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingState, setShippingState] = useState('');
  const [shippingCountry, setShippingCountry] = useState('');
  const [shippingZip, setShippingZip] = useState('');
  const [formError, setFormError] = useState('');

  // Payment
  const [enabledGateways, setEnabledGateways] = useState<GatewayOption[]>([]);
  const [selectedGateway, setSelectedGateway] = useState<'paystack' | 'flutterwave' | null>(null);
  const [loadingGateways, setLoadingGateways] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Preview
  const [previewProduct, setPreviewProduct] = useState<Product | null>(null);
  const [previewMediaIndex, setPreviewMediaIndex] = useState(0);

  useEffect(() => {
    if (userId) {
      setResolvedUserId(userId);
    } else {
      // Custom domain: resolve store by hostname (fallback if overrideUserId not provided)
      const hostname = window.location.hostname;
      supabase
        .from('store_profiles')
        .select('user_id')
        .eq('custom_domain', hostname)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.user_id) {
            setResolvedUserId(data.user_id);
          } else {
            setDomainNotFound(true);
            setLoading(false);
          }
        });
    }
  }, [userId]);

  useEffect(() => {
    if (resolvedUserId) fetchStoreData();
  }, [resolvedUserId]);

  const fetchStoreData = async () => {
    try {
      setLoading(true);
      const [{ data: storeData }, { data: userData }, { data: productsData }] = await Promise.all([
        supabase.from('store_profiles').select('*').eq('user_id', resolvedUserId).maybeSingle(),
        supabase.from('profiles').select('full_name, avatar_url').eq('id', resolvedUserId).maybeSingle(),
        supabase.from('store_products').select('*').eq('user_id', resolvedUserId).order('created_at', { ascending: false }),
      ]);
      setStoreProfile(storeData);
      setUserProfile(userData);
      setProducts(productsData || []);
    } catch (error) {
      console.error('Error fetching store data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnabledGateways = async () => {
    setLoadingGateways(true);
    try {
      const { data } = await supabase
        .from('admin_settings')
        .select('setting_value')
        .eq('setting_key', 'payment_revenue')
        .maybeSingle();

      if (data?.setting_value) {
        const enabled = (data.setting_value as Record<string, unknown>).enabledGateways as Record<string, boolean> || {};
        const filtered = allGateways.filter(g => enabled[g.id] === true);
        setEnabledGateways(filtered);
        if (filtered.length > 0) setSelectedGateway(filtered[0].id);
        else setSelectedGateway(null);
      } else {
        setEnabledGateways([]);
        setSelectedGateway(null);
      }
    } catch {
      setEnabledGateways([]);
      setSelectedGateway(null);
    } finally {
      setLoadingGateways(false);
    }
  };

  const handleBuyNow = (product: Product) => {
    setSelectedProduct(product);
    setShowPurchaseModal(true);
    setFormError('');
    setBuyerName(''); setBuyerEmail('');
    setShippingAddress(''); setShippingCity('');
    setShippingState(''); setShippingCountry(''); setShippingZip('');
    setSelectedGateway(null);
    setEnabledGateways([]);
    fetchEnabledGateways();
  };

  const handleCardClick = (product: Product) => {
    setPreviewProduct(product);
    setPreviewMediaIndex(0);
  };

  const handlePurchase = async () => {
    setFormError('');
    if (!buyerName.trim() || !buyerEmail.trim()) {
      setFormError('Please fill in your name and email.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(buyerEmail)) {
      setFormError('Please enter a valid email address.');
      return;
    }
    if (selectedProduct?.product_type === 'physical') {
      if (!shippingAddress.trim() || !shippingCity.trim() || !shippingCountry.trim()) {
        setFormError('Please fill in all required shipping fields.');
        return;
      }
    }
    if (!selectedGateway) {
      setFormError('Please select a payment method.');
      return;
    }
    if (!selectedProduct) return;

    try {
      setProcessing(true);

      const isPhysical = selectedProduct.product_type === 'physical';
      const orderPayload: Record<string, unknown> = {
        product_id: selectedProduct.id, seller_id: resolvedUserId,
        buyer_name: buyerName, buyer_email: buyerEmail,
        amount: selectedProduct.price, currency: selectedProduct.currency,
        status: 'pending', product_type: selectedProduct.product_type,
        fulfillment_status: isPhysical ? 'unfulfilled' : null,
      };
      if (isPhysical) {
        orderPayload.shipping_address = shippingAddress;
        orderPayload.shipping_city = shippingCity;
        orderPayload.shipping_state = shippingState;
        orderPayload.shipping_country = shippingCountry;
        orderPayload.shipping_zip = shippingZip;
      }

      const { data: order, error: orderError } = await supabase
        .from('store_orders').insert(orderPayload).select().maybeSingle();
      if (orderError) throw orderError;

      const callbackUrl = `${window.location.origin}/payment-success?type=store_product&order_id=${order?.id}`;
      const txRef = `store_${order?.id}_${Date.now()}`;

      if (selectedGateway === 'paystack') {
        const { data: paymentData, error: paymentError } = await supabase.functions.invoke('paystack-initialize', {
          body: {
            email: buyerEmail,
            amount: selectedProduct.price,
            currency: selectedProduct.currency,
            callback_url: callbackUrl,
            metadata: {
              order_id: order?.id,
              product_id: selectedProduct.id,
              product_title: selectedProduct.title,
              buyer_name: buyerName,
              type: 'store_product',
            },
          },
        });
        if (paymentError) throw paymentError;
        if (paymentData?.authorization_url) {
          window.location.href = paymentData.authorization_url;
        } else {
          throw new Error(paymentData?.error || 'Failed to get payment URL from Paystack.');
        }
      } else {
        const { data: paymentData, error: paymentError } = await supabase.functions.invoke('flutterwave-initialize', {
          body: {
            email: buyerEmail,
            name: buyerName,
            amount: selectedProduct.price,
            currency: selectedProduct.currency,
            tx_ref: txRef,
            redirect_url: callbackUrl,
            metadata: {
              order_id: order?.id,
              product_id: selectedProduct.id,
              product_title: selectedProduct.title,
              buyer_name: buyerName,
              type: 'store_product',
            },
          },
        });
        if (paymentError) throw paymentError;
        const redirectLink = paymentData?.payment_link || paymentData?.link;
        if (redirectLink) {
          window.location.href = redirectLink;
        } else {
          throw new Error(paymentData?.error || 'Failed to get payment URL from Flutterwave.');
        }
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to process purchase. Please try again.';
      setFormError(msg);
    } finally {
      setProcessing(false);
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesFilter = activeFilter === 'all' || p.product_type === activeFilter;
    const matchesSearch = !searchQuery || p.title.toLowerCase().includes(searchQuery.toLowerCase()) || p.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const digitalCount = products.filter(p => p.product_type !== 'physical').length;
  const physicalCount = products.filter(p => p.product_type === 'physical').length;
  const storeName = storeProfile?.store_name || `${userProfile?.full_name || 'My'}'s Store`;
  const storeDesc = storeProfile?.store_description || 'Welcome to my store. Browse and purchase high-quality products.';

  const activeTemplate = storeProfile?.template || 'classic';

  const templateProps = {
    storeProfile,
    userProfile,
    products,
    storeName,
    storeDesc,
    onBuyNow: handleBuyNow,
    onCardClick: handleCardClick,
    searchQuery,
    setSearchQuery,
    activeFilter,
    setActiveFilter,
    filteredProducts,
  };

  if (domainNotFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-global-line text-4xl text-gray-400"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-3">Store Not Found</h1>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">
            No store is linked to <strong className="font-mono text-gray-700">{window.location.hostname}</strong>. The domain may not be configured yet or the DNS is still propagating.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-500 text-white rounded-xl text-sm font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap">
            <i className="ri-home-line"></i> Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading store...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Back to home nav — shown on all templates */}
      <div className="fixed top-4 left-4 z-50">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/90 backdrop-blur border border-gray-200 rounded-full text-xs font-medium text-gray-600 hover:text-teal-600 shadow-sm transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Back to Home
        </Link>
      </div>

      {/* Render selected template */}
      {activeTemplate === 'dark' && <DarkTemplate {...templateProps} />}
      {activeTemplate === 'minimal' && <MinimalTemplate {...templateProps} />}
      {activeTemplate === 'boutique' && <BoutiqueTemplate {...templateProps} />}
      {activeTemplate === 'vibrant' && <VibrantTemplate {...templateProps} />}
      {activeTemplate === 'grid' && <GridTemplate {...templateProps} />}
      {(activeTemplate === 'classic' || !['dark','minimal','boutique','vibrant','grid'].includes(activeTemplate)) && (
        <ClassicTemplate {...templateProps} />
      )}

      {/* ── Product Preview Modal ── */}
      {previewProduct && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm" onClick={() => setPreviewProduct(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10 rounded-t-2xl">
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${previewProduct.product_type === 'physical' ? 'bg-orange-100 text-orange-700' : 'bg-teal-100 text-teal-700'}`}>
                  <i className={previewProduct.product_type === 'physical' ? 'ri-box-3-line' : 'ri-download-cloud-line'}></i>
                  {previewProduct.product_type === 'physical' ? 'Physical' : 'Digital'}
                </span>
                <h2 className="text-base font-bold text-gray-900 line-clamp-1">{previewProduct.title}</h2>
              </div>
              <button onClick={() => setPreviewProduct(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer rounded-full hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-5">
              {(() => {
                const mediaList = previewProduct.media_files && previewProduct.media_files.length > 0
                  ? previewProduct.media_files
                  : previewProduct.cover_image
                    ? [{ url: previewProduct.cover_image, type: isVideoUrl(previewProduct.cover_image) ? 'video' as const : 'image' as const, name: 'cover' }]
                    : [];
                if (mediaList.length === 0) {
                  return (
                    <div className="w-full h-64 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center">
                      <i className={`${categoryIcons[previewProduct.category] || 'ri-file-line'} text-6xl text-gray-300 mb-2`}></i>
                      <span className="text-sm text-gray-400 capitalize">{previewProduct.category || 'Product'}</span>
                    </div>
                  );
                }
                const current = mediaList[previewMediaIndex];
                const isVid = current.type === 'video' || isVideoUrl(current.url);
                return (
                  <div>
                    <div className="relative w-full rounded-xl overflow-hidden bg-black flex items-center justify-center">
                      {isVid ? (
                        <video key={current.url} src={current.url} className="w-full max-h-[70vh] object-contain" controls autoPlay playsInline style={{ display: 'block' }} />
                      ) : (
                        <img src={current.url} alt={previewProduct.title} className="w-full max-h-[70vh] object-contain" />
                      )}
                    </div>
                    {mediaList.length > 1 && (
                      <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                        {mediaList.map((m, idx) => {
                          const mIsVid = m.type === 'video' || isVideoUrl(m.url);
                          return (
                            <button key={idx} onClick={() => setPreviewMediaIndex(idx)} className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${previewMediaIndex === idx ? 'border-teal-500 shadow-md' : 'border-gray-200 hover:border-gray-400'}`}>
                              {mIsVid ? (
                                <div className="w-full h-full bg-gray-900 flex items-center justify-center"><i className="ri-play-circle-fill text-white text-2xl"></i></div>
                              ) : (
                                <img src={m.url} alt={`media-${idx}`} className="w-full h-full object-cover" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{previewProduct.title}</h3>
                <div className="text-2xl font-extrabold text-teal-600 mb-3">{previewProduct.currency} {previewProduct.price.toLocaleString()}</div>
                {previewProduct.description && <p className="text-gray-500 text-sm leading-relaxed">{previewProduct.description}</p>}
              </div>
              {previewProduct.product_type === 'physical' && (
                <div className="space-y-2">
                  {previewProduct.shipping_info && (
                    <div className="flex items-start gap-2 bg-orange-50 rounded-xl px-4 py-3 border border-orange-100">
                      <i className="ri-truck-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                      <div>
                        <p className="text-xs font-semibold text-orange-700 mb-0.5">Shipping Info</p>
                        <p className="text-xs text-orange-600">{previewProduct.shipping_info}</p>
                      </div>
                    </div>
                  )}
                  {!previewProduct.stock_unlimited && (
                    <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                      <i className="ri-stack-line text-gray-400 flex-shrink-0"></i>
                      <span className={`text-sm font-medium ${previewProduct.stock_quantity <= 5 ? 'text-red-500' : 'text-gray-600'}`}>
                        {previewProduct.stock_quantity <= 0 ? 'Out of Stock' : previewProduct.stock_quantity <= 5 ? `Only ${previewProduct.stock_quantity} left!` : `${previewProduct.stock_quantity} in stock`}
                      </span>
                    </div>
                  )}
                  {previewProduct.weight > 0 && (
                    <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                      <i className="ri-scales-line text-gray-400 flex-shrink-0"></i>
                      <span className="text-sm text-gray-600">Weight: {previewProduct.weight} kg</span>
                    </div>
                  )}
                </div>
              )}
              {(() => {
                const isPhysical = previewProduct.product_type === 'physical';
                const outOfStock = !previewProduct.stock_unlimited && previewProduct.stock_quantity <= 0;
                return (
                  <button
                    onClick={() => { setPreviewProduct(null); if (!outOfStock) handleBuyNow(previewProduct); }}
                    disabled={outOfStock}
                    className={`w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all whitespace-nowrap cursor-pointer shadow-sm ${outOfStock ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : isPhysical ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-teal-500 text-white hover:bg-teal-600'}`}
                  >
                    <i className={isPhysical ? 'ri-shopping-cart-line' : 'ri-download-line'}></i>
                    {outOfStock ? 'Out of Stock' : isPhysical ? 'Buy Now' : 'Get Now'}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Purchase Modal ── */}
      {showPurchaseModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto relative shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10 rounded-t-2xl">
              <h2 className="text-lg font-bold text-gray-900">Complete Purchase</h2>
              <button onClick={() => setShowPurchaseModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer rounded-full hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="bg-gray-50 rounded-xl p-4 flex gap-4 border border-gray-100">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-200 flex-shrink-0">
                  {selectedProduct.cover_image || (selectedProduct.media_files && selectedProduct.media_files.length > 0) ? (
                    <CoverMedia product={selectedProduct} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className={`${categoryIcons[selectedProduct.category] || 'ri-file-line'} text-2xl text-gray-400`}></i>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mb-1 ${selectedProduct.product_type === 'physical' ? 'bg-orange-100 text-orange-700' : 'bg-teal-100 text-teal-700'}`}>
                    <i className={selectedProduct.product_type === 'physical' ? 'ri-box-3-line' : 'ri-download-cloud-line'}></i>
                    {selectedProduct.product_type === 'physical' ? 'Physical' : 'Digital'}
                  </span>
                  <h3 className="font-semibold text-gray-900 text-sm line-clamp-1">{selectedProduct.title}</h3>
                  <div className="text-lg font-extrabold text-teal-600 mt-0.5">{selectedProduct.currency} {selectedProduct.price.toLocaleString()}</div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <i className="ri-user-line text-teal-500"></i> Your Details
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Full Name <span className="text-red-500">*</span></label>
                    <input type="text" value={buyerName} onChange={e => setBuyerName(e.target.value)} placeholder="Enter your full name"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Email Address <span className="text-red-500">*</span></label>
                    <input type="email" value={buyerEmail} onChange={e => setBuyerEmail(e.target.value)} placeholder="Enter your email"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none text-sm" />
                  </div>
                </div>
              </div>

              {selectedProduct.product_type === 'physical' && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <i className="ri-map-pin-line text-orange-500"></i> Shipping Address
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Street Address <span className="text-red-500">*</span></label>
                      <input type="text" value={shippingAddress} onChange={e => setShippingAddress(e.target.value)} placeholder="e.g. 12 Lagos Street, Flat 3"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">City <span className="text-red-500">*</span></label>
                        <input type="text" value={shippingCity} onChange={e => setShippingCity(e.target.value)} placeholder="City"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">State / Province</label>
                        <input type="text" value={shippingState} onChange={e => setShippingState(e.target.value)} placeholder="State"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none text-sm" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Country <span className="text-red-500">*</span></label>
                        <input type="text" value={shippingCountry} onChange={e => setShippingCountry(e.target.value)} placeholder="Country"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">ZIP / Postal Code</label>
                        <input type="text" value={shippingZip} onChange={e => setShippingZip(e.target.value)} placeholder="ZIP"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none text-sm" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <i className="ri-bank-card-2-line text-teal-500"></i> Payment Method <span className="text-red-500">*</span>
                </h3>
                {loadingGateways ? (
                  <div className="flex items-center gap-3 py-4 px-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="w-5 h-5 border-2 border-teal-200 border-t-teal-500 rounded-full animate-spin flex-shrink-0"></div>
                    <p className="text-sm text-gray-400">Loading payment options...</p>
                  </div>
                ) : enabledGateways.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center space-y-1">
                    <i className="ri-error-warning-line text-xl text-amber-500"></i>
                    <p className="text-sm font-semibold text-amber-900">No Payment Methods Available</p>
                    <p className="text-xs text-amber-700">The store owner has not enabled any payment gateways.</p>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {enabledGateways.map(gw => {
                      const isSelected = selectedGateway === gw.id;
                      return (
                        <button
                          key={gw.id}
                          onClick={() => { setSelectedGateway(gw.id); setFormError(''); }}
                          disabled={processing}
                          className={`relative flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-all duration-200 cursor-pointer text-left w-full disabled:opacity-50 ${isSelected ? `${gw.borderColor} ${gw.bgColor} shadow-sm` : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`}
                        >
                          {isSelected && (
                            <div className="absolute top-2 right-2 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                              <i className="ri-check-line text-white text-xs"></i>
                            </div>
                          )}
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? gw.bgColor : 'bg-gray-100'}`}>
                            <i className={`${gw.icon} text-xl ${isSelected ? gw.accentColor : 'text-gray-400'}`}></i>
                          </div>
                          <div>
                            <p className={`text-sm font-semibold ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>{gw.name}</p>
                            <p className="text-xs text-gray-500">{gw.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className={`rounded-xl p-3 flex gap-3 ${selectedProduct.product_type === 'physical' ? 'bg-orange-50 border border-orange-100' : 'bg-teal-50 border border-teal-100'}`}>
                <i className={`text-lg flex-shrink-0 ${selectedProduct.product_type === 'physical' ? 'ri-truck-line text-orange-500' : 'ri-download-cloud-line text-teal-500'}`}></i>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {selectedProduct.product_type === 'physical'
                    ? (selectedProduct.shipping_info || 'Your order will be shipped to the address provided after payment is confirmed.')
                    : "After successful payment, you'll receive a download link via email instantly."}
                </p>
              </div>

              <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                <div className="w-7 h-7 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-lock-line text-teal-600 text-xs"></i>
                </div>
                <p className="text-xs text-gray-500">Your payment is encrypted and secure. We never store your card details.</p>
              </div>

              {formError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <i className="ri-error-warning-line text-red-500"></i>
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}

              <button
                onClick={handlePurchase}
                disabled={processing || loadingGateways || enabledGateways.length === 0 || !selectedGateway}
                className="w-full py-3.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 disabled:bg-gray-200 disabled:cursor-not-allowed transition-colors font-bold whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer text-sm shadow-sm"
              >
                {processing ? (
                  <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Processing...</>
                ) : (
                  <><i className="ri-secure-payment-line text-base"></i>Pay {selectedProduct.currency} {selectedProduct.price.toLocaleString()}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Classic Template (inline, original design) ──
function ClassicTemplate({ storeProfile, userProfile, products, storeName, storeDesc, onBuyNow, onCardClick, searchQuery, setSearchQuery, activeFilter, setActiveFilter, filteredProducts }: {
  storeProfile: StoreProfile | null;
  userProfile: Profile | null;
  products: Product[];
  storeName: string;
  storeDesc: string;
  onBuyNow: (p: Product) => void;
  onCardClick: (p: Product) => void;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  activeFilter: 'all' | 'digital' | 'physical';
  setActiveFilter: (v: 'all' | 'digital' | 'physical') => void;
  filteredProducts: Product[];
}) {
  const digitalCount = products.filter(p => p.product_type !== 'physical').length;
  const physicalCount = products.filter(p => p.product_type === 'physical').length;
  const activeLinks = (storeProfile?.social_links || []).filter(l => l.is_active);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Hero Banner */}
      <div className="relative w-full h-80 overflow-hidden">
        {storeProfile?.banner_image ? (
          <img src={storeProfile.banner_image} alt="Store banner" className="w-full h-full object-cover object-top" />
        ) : (
          <img
            src="https://public.readdy.ai/ai/img_res/1bf5a552c487b101d82c0c94c41c661c.jpg"
            alt="Store banner"
            className="w-full h-full object-cover object-top"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="relative -mt-6 z-10 mb-8">
          <div className="absolute -top-10 left-6 z-20">
            <div className="w-24 h-24 rounded-2xl overflow-hidden border-4 border-white shadow-xl bg-white">
              <img
                src={storeProfile?.logo_image || userProfile?.avatar_url || 'https://readdy.ai/api/search-image?query=professional%20business%20store%20logo%20icon%20minimal%20clean%20design%20on%20white%20background&width=200&height=200&seq=store-default-logo&orientation=squarish'}
                alt="Store logo"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-md overflow-hidden">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6 p-6 pt-16">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <h2 className="text-xl font-bold text-gray-900">{storeName}</h2>
                  <span className="flex items-center gap-1 text-xs bg-teal-50 text-teal-700 px-2.5 py-1 rounded-full font-medium border border-teal-100">
                    <i className="ri-verified-badge-line"></i> Verified Store
                  </span>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed mb-3 max-w-2xl">{storeDesc}</p>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1.5 text-sm text-gray-500 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-100">
                    <i className="ri-store-2-line text-gray-400"></i>
                    <span><strong className="text-gray-700">{products.length}</strong> {products.length === 1 ? 'Product' : 'Products'}</span>
                  </div>
                  {digitalCount > 0 && (
                    <div className="flex items-center gap-1.5 text-sm text-teal-700 bg-teal-50 px-3 py-1.5 rounded-full border border-teal-100">
                      <i className="ri-download-cloud-line"></i>
                      <span><strong>{digitalCount}</strong> Digital</span>
                    </div>
                  )}
                  {physicalCount > 0 && (
                    <div className="flex items-center gap-1.5 text-sm text-orange-700 bg-orange-50 px-3 py-1.5 rounded-full border border-orange-100">
                      <i className="ri-box-3-line"></i>
                      <span><strong>{physicalCount}</strong> Physical</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="border-t border-gray-100 px-6 py-3 bg-gray-50/60 flex flex-wrap gap-6">
              <div className="flex items-center gap-2 text-sm text-gray-500"><i className="ri-star-line text-yellow-400"></i><span>Quality Products</span></div>
              <div className="flex items-center gap-2 text-sm text-gray-500"><i className="ri-secure-payment-line text-teal-400"></i><span>Safe Checkout</span></div>
              <div className="flex items-center gap-2 text-sm text-gray-500"><i className="ri-mail-send-line text-teal-400"></i><span>Instant Delivery for Digital</span></div>
            </div>
          </div>
        </div>

        {/* ── Linktree Section ── */}
        {activeLinks.length > 0 && (
          <div className="mb-8 bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-5">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-links-line text-teal-500 text-base"></i>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Links</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {activeLinks.map(link => {
                const meta = LINK_PLATFORMS[link.platform] || LINK_PLATFORMS.custom;
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 hover:border-teal-300 hover:bg-teal-50 transition-all group cursor-pointer"
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 flex-shrink-0 ${meta.color}`}>
                      <i className={`${meta.icon} text-base`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate group-hover:text-teal-700 transition-colors">{link.label}</p>
                    </div>
                    <i className="ri-external-link-line text-gray-300 group-hover:text-teal-400 transition-colors text-xs flex-shrink-0"></i>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search products..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white" />
          </div>
          <div className="flex items-center gap-2">
            {(['all', 'digital', 'physical'] as const).map(f => (
              <button key={f} onClick={() => setActiveFilter(f)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeFilter === f ? 'bg-teal-500 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:border-teal-300 hover:text-teal-600'}`}>
                {f === 'all' ? `All (${products.length})` : f === 'digital' ? `Digital (${digitalCount})` : `Physical (${physicalCount})`}
              </button>
            ))}
          </div>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-20 text-center mb-12 border border-gray-100">
            <i className="ri-store-2-line text-5xl text-gray-200 mb-4 block"></i>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{searchQuery ? 'No products found' : 'No Products Yet'}</h3>
            <p className="text-gray-500 max-w-sm mx-auto text-sm leading-relaxed">
              {searchQuery ? `No products match "${searchQuery}".` : "This store doesn't have any products available yet."}
            </p>
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="mt-4 px-5 py-2 bg-teal-500 text-white rounded-full text-sm font-medium hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap">
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-400 mb-4">{filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pb-16">
              {filteredProducts.map((product) => {
                const isPhysical = product.product_type === 'physical';
                const outOfStock = !product.stock_unlimited && product.stock_quantity <= 0;
                return (
                  <div key={product.id} onClick={() => onCardClick(product)}
                    className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group border border-gray-100 flex flex-col cursor-pointer">
                    <div className="relative overflow-hidden bg-gray-100 flex-shrink-0">
                      {product.cover_image || (product.media_files && product.media_files.length > 0) ? (
                        <>
                          <CoverMedia product={product} natural className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-500" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs font-semibold text-gray-800 shadow">
                              <i className="ri-eye-line"></i> Preview
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-52 flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                          <i className={`${categoryIcons[product.category] || 'ri-file-line'} text-5xl text-gray-300 mb-2`}></i>
                        </div>
                      )}
                      <div className="absolute top-3 left-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-sm ${isPhysical ? 'bg-orange-500 text-white' : 'bg-teal-500 text-white'}`}>
                          <i className={isPhysical ? 'ri-box-3-line' : 'ri-download-cloud-line'}></i>
                          {isPhysical ? 'Physical' : 'Digital'}
                        </span>
                      </div>
                      {outOfStock && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <span className="bg-white text-gray-800 font-bold px-4 py-2 rounded-full text-sm shadow">Out of Stock</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <h3 className="text-sm font-bold text-gray-900 line-clamp-2 mb-1 leading-snug">{product.title}</h3>
                      <p className="text-gray-400 text-xs mb-3 line-clamp-2 leading-relaxed flex-1">{product.description}</p>
                      {isPhysical && product.shipping_info && (
                        <div className="flex items-start gap-1.5 mb-3 bg-orange-50 rounded-lg px-2.5 py-2">
                          <i className="ri-truck-line text-orange-400 text-xs mt-0.5 flex-shrink-0"></i>
                          <p className="text-xs text-orange-600 line-clamp-1">{product.shipping_info}</p>
                        </div>
                      )}
                      {!product.stock_unlimited && isPhysical && product.stock_quantity > 0 && (
                        <div className="flex items-center gap-1.5 mb-3">
                          <i className="ri-stack-line text-gray-300 text-xs"></i>
                          <span className={`text-xs font-medium ${product.stock_quantity <= 5 ? 'text-red-500' : 'text-gray-400'}`}>
                            {product.stock_quantity <= 5 ? `Only ${product.stock_quantity} left!` : `${product.stock_quantity} in stock`}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-50">
                        <div className="text-lg font-extrabold text-gray-900">
                          {product.currency} {product.price.toLocaleString()}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); if (!outOfStock) onBuyNow(product); }}
                          disabled={outOfStock}
                          className={`px-4 py-2 rounded-xl transition-all font-semibold whitespace-nowrap flex items-center gap-1.5 text-xs cursor-pointer shadow-sm ${outOfStock ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : isPhysical ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-teal-500 text-white hover:bg-teal-600'}`}
                        >
                          <i className={isPhysical ? 'ri-shopping-cart-line' : 'ri-download-line'}></i>
                          {isPhysical ? 'Buy Now' : 'Get Now'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <footer className="bg-white border-t border-gray-100 py-8 mt-4">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <i className="ri-store-2-line text-teal-400"></i>
            <span>Powered by <strong className="text-gray-600">EventHub Store</strong></span>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><i className="ri-shield-check-line text-teal-400"></i> Secure Payments</span>
            <span className="flex items-center gap-1"><i className="ri-lock-line text-teal-400"></i> Buyer Protected</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
